import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import Pill from 'fe-design-base/molecules/Pill';
import { PillVariant } from 'fe-design-base/molecules/Pill/Pill';

import {
  approveTradeCoverRequest,
  rejectTradeCoverRequest,
} from 'features/notifications/api';
import { ShiftDetails } from 'features/notifications/components/ShiftDetails';
import { useRequestAction } from 'features/notifications/hooks/useRequestAction';
import { ShiftBlock, TradeShiftCardProps } from 'features/notifications/types';

import { convertToTimezone, prettyDayAtTimeLowercase } from 'util/dateTime';
import { toI18n } from 'util/i18n';

type Status = 'rejected' | 'declined' | 'approved' | 'accepted';

const statuses: Record<Status, { label: string; variant: PillVariant }> = {
  accepted: {
    label: toI18n('requests_feed.statuses.pending_approval'),
    variant: 'warning',
  },
  declined: {
    label: toI18n('requests_feed.trade.statuses.declined'),
    variant: 'error',
  },
  rejected: {
    label: toI18n('requests_feed.statuses.denied_by_manager'),
    variant: 'error',
  },
  approved: {
    label: toI18n('requests_feed.statuses.approved'),
    variant: 'success',
  },
};

const TradeShiftCard = ({
  claim,
  shift,
  location,
  requester,
  viewer_role: viewerRole,
}: TradeShiftCardProps) => {
  const { isLoading: isApproving, handleAction: handleApprove } =
    useRequestAction({
      apiFunction: approveTradeCoverRequest,
      itemId: claim.id,
      successMessage: 'requests_feed.trade.details.approved_success',
      initiatedFrom: 'trade_shift_card',
      buttonText: toI18n('requests_feed.actions.approve'),
    });
  const { isLoading: isDenying, handleAction: handleDeny } = useRequestAction({
    apiFunction: rejectTradeCoverRequest,
    itemId: claim.id,
    successMessage: 'requests_feed.trade.details.denied_success',
    initiatedFrom: 'trade_shift_card',
    buttonText: toI18n('requests_feed.actions.deny'),
  });

  const { responder, status } = claim;
  const name = `${responder?.first_name} ${responder?.last_name}`;
  const requesterName = `${requester.first_name} ${requester.last_name}`;
  const offeredShift = claim.details?.offered_shift as ShiftBlock;
  const acceptedAtSecs = claim.details?.accepted_at_secs || 0;
  const reason = claim.details?.reason;
  const showPill =
    viewerRole === 'requester' ||
    (status !== 'accepted' && status !== 'rejected');

  const showButtons = viewerRole === 'oam' && status !== 'rejected';

  // Convert accepted_at_secs to location timezone
  const timezone = (location as any)?.tz_name;
  const acceptedAtInTz = convertToTimezone(acceptedAtSecs, timezone);

  return (
    <Box bw={1} bcolor="mono400" bradiusm p={16} bgcolor="mono0" gap={8} column>
      {showPill && (
        <Pill variant={statuses[status as Status].variant}>
          {statuses[status as Status].label}
        </Pill>
      )}
      <Box column gap={8}>
        <Box column>
          <Text
            variant="bodyBoldTight"
            i18n="requests_feed.trade.details.requester_name"
            i18nProps={{ requester_name: requesterName }}
          />
          <ShiftDetails shift={offeredShift} location={location} />
        </Box>
        <Box column>
          <Text
            variant="bodyBoldTight"
            i18n="requests_feed.trade.details.responder_name"
            i18nProps={{ responder_name: name }}
          />
          <ShiftDetails shift={shift} location={location} />
        </Box>
        <Box row gap={4} alignItemsCenter>
          <Text variant="bodySmBold" color="mono700">
            {toI18n('requests_feed.trade.details.accepted')}
          </Text>
          <Text variant="bodySm" color="mono700">
            {prettyDayAtTimeLowercase(acceptedAtInTz)}
          </Text>
        </Box>
      </Box>

      {reason && (
        <Box column gap={2}>
          <Text variant="bodySmBold" color="mono700">
            {toI18n('requests_feed.trade.note')}
          </Text>
          <Text variant="bodySm" color="mono700">
            {reason}
          </Text>
        </Box>
      )}

      {showButtons && (
        <Box row gap={4} grow={1}>
          <Button
            fullWidth
            variant="secondary"
            onClick={handleDeny}
            isLoading={isDenying}
            disabled={isApproving || isDenying}
          >
            {toI18n('requests_feed.actions.deny')}
          </Button>
          <Button
            fullWidth
            onClick={handleApprove}
            isLoading={isApproving}
            disabled={isDenying || isApproving}
          >
            {toI18n('requests_feed.actions.approve')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TradeShiftCard;
